import jsPDF from 'jspdf';
import { parseConclusion, type ConclusionSection } from './conclusionParser';
import { buildCaseSheetConclusionSections, type CaseSheetSummaryInput } from './caseSheetSummary';
import { formatVitals, type VitalSigns } from '@/lib/types/consultation';
import { stripHtml } from '@/lib/api/medplum-resources';

/**
 * Draws a list of already-parsed conclusion sections (text and table sections)
 * starting at yPos, paginating as needed. Shared by generateDiagnosticReportsPDF
 * and generateCaseSheetPDF so both PDFs render medications/diagnosis as tables
 * through the exact same layout code.
 */
function renderConclusionSections(
  pdf: jsPDF,
  sections: ConclusionSection[],
  startYPos: number,
  pageWidth: number,
  pageHeight: number,
  topMargin: number,
  bottomMargin: number,
  lineHeight: number
): number {
  let yPos = startYPos;

  const writeTextLines = (lines: string[]) => {
    for (const line of lines) {
      if (yPos + lineHeight > pageHeight - bottomMargin) {
        pdf.addPage();
        yPos = topMargin;
      }
      pdf.text(String(line), 20, yPos);
      yPos += lineHeight;
    }
  };

  for (const section of sections) {
    // Check if we need a new page for the section header
    if (yPos + lineHeight * 2 > pageHeight - bottomMargin) {
      pdf.addPage();
      yPos = topMargin;
    }

    // Section header
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(section.label + ':', 20, yPos);
    yPos += lineHeight + 2;

    // Handle table sections (e.g., medications)
    if (section.type === 'table' && section.columns && section.rows) {
      const tableLeft = 20;
      const tableWidth = pageWidth - 40;

      // Define column width ratios for better readability
      const colWidthRatios: Record<string, number> = {
        name: 0.25,
        medicineType: 0.08,
        dosage: 0.08,
        frequency: 0.1,
        route: 0.08,
        duration: 0.07,
        relationWithMeal: 0.13,
        comments: 0.11,
        itemCode: 0.1,
      };

      // Calculate actual column widths
      const colWidths = section.columns.map((col) => {
        const ratio = colWidthRatios[col.key] || 1 / (section.columns?.length || 1);
        return tableWidth * ratio;
      });

      const rowHeight = lineHeight + 6;

      // Draw table header with multi-line support
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 240, 240);
      pdf.rect(tableLeft, yPos, tableWidth, rowHeight, 'F');
      let xOffset = tableLeft;
      section.columns.forEach((col, idx) => {
        // Split long headers into multiple lines
        const headerLines = pdf.splitTextToSize(col.label, colWidths[idx] - 2);
        headerLines.forEach((line: string, lineIdx: number) => {
          pdf.text(line, xOffset + 1, yPos + 3 + lineIdx * 3);
        });
        xOffset += colWidths[idx];
      });
      yPos += rowHeight;

      // Draw table rows
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      for (const row of section.rows) {
        // Pre-compute wrapped lines for all cells (reuse for both height calculation and drawing)
        const wrappedCells: string[][] = [];
        let maxLines = 1;
        section.columns.forEach((col, idx) => {
          const cellValue = stripHtml(row[col.key] || '');
          const cellText = pdf.splitTextToSize(cellValue, colWidths[idx] - 2);
          wrappedCells.push(cellText);
          maxLines = Math.max(maxLines, cellText.length);
        });

        const actualRowHeight = rowHeight * maxLines;

        // Check if we need a new page
        if (yPos + actualRowHeight > pageHeight - bottomMargin) {
          pdf.addPage();
          yPos = topMargin;
          // Redraw header on new page with correct font size
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.setFillColor(240, 240, 240);
          pdf.rect(tableLeft, yPos, tableWidth, rowHeight, 'F');
          xOffset = tableLeft;
          section.columns.forEach((col, idx) => {
            const headerLines = pdf.splitTextToSize(col.label, colWidths[idx] - 2);
            headerLines.forEach((line: string, lineIdx: number) => {
              pdf.text(line, xOffset + 1, yPos + 3 + lineIdx * 3);
            });
            xOffset += colWidths[idx];
          });
          yPos += rowHeight;
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
        }

        // Draw row with text wrapping using pre-computed lines
        xOffset = tableLeft;
        wrappedCells.forEach((cellText, idx) => {
          cellText.forEach((line: string, lineIdx: number) => {
            pdf.text(line, xOffset + 1, yPos + 4 + lineIdx * (lineHeight + 1));
          });
          xOffset += colWidths[idx];
        });
        yPos += actualRowHeight;
      }

      // Add spacing after table
      yPos += lineHeight;
    } else if (section.type === 'list' && section.items && section.items.length > 0) {
      // Bullet list — one entry per item, wrapped continuation lines indented under the bullet
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      for (const item of section.items) {
        const cleanItem = stripHtml(item);
        // A bare "Label:" line (e.g. "Diet Advice:") is a sub-heading, not a
        // bulleted entry — render it plain and unindented, like the section
        // label above it.
        const isSubHeading = /^[A-Za-z][A-Za-z &'/-]{1,40}:$/.test(cleanItem.trim());
        if (isSubHeading) {
          if (yPos + lineHeight > pageHeight - bottomMargin) {
            pdf.addPage();
            yPos = topMargin;
          }
          pdf.text(cleanItem, 20, yPos);
          yPos += lineHeight;
          continue;
        }
        const wrapped = pdf.splitTextToSize(cleanItem, pageWidth - 51);
        wrapped.forEach((line: string, lineIdx: number) => {
          if (yPos + lineHeight > pageHeight - bottomMargin) {
            pdf.addPage();
            yPos = topMargin;
          }
          pdf.text(lineIdx === 0 ? `• ${line}` : `  ${line}`, 25, yPos);
          yPos += lineHeight;
        });
      }

      // Add spacing between sections
      yPos += lineHeight;
    } else {
      // Section content (text) — strip any HTML tags (e.g. <b>) that the
      // AI-generated conclusion may contain; jsPDF renders plain text only.
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const plainContent = stripHtml(section.content);
      const contentLines = pdf.splitTextToSize(plainContent, pageWidth - 40);
      writeTextLines(contentLines);

      // Add spacing between sections
      yPos += lineHeight;
    }
  }

  return yPos;
}

/**
 * Interface for diagnostic report data used in PDF generation
 */
export interface DiagnosticReportData {
  id: string;
  title?: string;
  issued?: string;
  conclusion?: string;
  status?: string;
  patientName?: string;
  patientId?: string;
  practitionerName?: string;
  vitals?: VitalSigns;
  /** Explicit visit number to show in the header (e.g. "Visit 52"). When
   *  omitted, falls back to `reports.length - index` — correct only when the
   *  full, correctly-ordered visit history is passed in; a single-report
   *  download must always set this explicitly, or it prints "Visit 1". */
  visitNumber?: number;
  /** This "report" is actually the cross-visit aggregated patient summary,
   *  not one specific visit — suppress the "Visit N:" header prefix and
   *  print `title` on its own. */
  isAggregate?: boolean;
}

/**
 * Generate PDF from diagnostic reports
 */
export const generateDiagnosticReportsPDF = (
  reports: DiagnosticReportData[],
  patientInfo?: {
    name: string;
    id: string;
    dateOfBirth?: string;
    age?: number;
    gender?: string;
  }
): Promise<void> => {
  return new Promise((resolve) => {
    if (!reports || reports.length === 0) {
      throw new Error('No diagnostic reports provided');
    }

    const pdf = new jsPDF();

    // Set font
    pdf.setFont('helvetica');

    // Title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Diagnostic Reports', 20, 30);

    // Patient information
    if (patientInfo) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Patient: ${patientInfo.name}`, 20, 50);
      pdf.text(`Patient ID: ${patientInfo.id}`, 20, 60);

      let yPos = 70;
      if (patientInfo.dateOfBirth) {
        pdf.text(`Date of Birth: ${patientInfo.dateOfBirth}`, 20, yPos);
        yPos += 10;
      }
      if (patientInfo.age && patientInfo.gender) {
        pdf.text(`Age/Gender: ${patientInfo.age}/${patientInfo.gender}`, 20, yPos);
        yPos += 10;
      }
    }

    let yPos = patientInfo ? 90 : 50;

    // Add each report
    reports.forEach((report, index) => {
      // Check if we need a new page
      const pageHeight = pdf.internal.pageSize.height;
      const pageWidth = pdf.internal.pageSize.width;
      const bottomMargin = 30;
      const topMargin = 30;
      const lineHeight = 6; // reasonable default line height for size 10

      if (yPos > pageHeight - bottomMargin) {
        pdf.addPage();
        yPos = topMargin;
      }

      // Report header - show as Visit N instead of Report N
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      if (report.isAggregate) {
        pdf.text(report.title || 'Overall Patient Summary', 20, yPos);
      } else {
        // Reverse order: newest visit gets highest number. Prefer the caller's
        // explicit visitNumber (needed for a single-report download, where
        // reports.length is always 1 and this formula would always say "Visit 1").
        const visitNumber = report.visitNumber ?? reports.length - index;
        pdf.text(`Visit ${visitNumber}: ${report.title || 'Consultation Summary'}`, 20, yPos);
      }
      yPos += 15;

      // Report details
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');

      if (report.issued) {
        pdf.text(`Date: ${new Date(report.issued).toLocaleDateString()}`, 20, yPos);
        yPos += 10;
      }

      if (report.status) {
        pdf.text(`Status: ${report.status}`, 20, yPos);
        yPos += 10;
      }

      if (report.practitionerName) {
        pdf.text(`Practitioner: ${report.practitionerName}`, 20, yPos);
        yPos += 10;
      }

      // Vital Signs
      const vitalsLines = report.vitals ? formatVitals(report.vitals) : [];
      if (vitalsLines.length > 0) {
        if (yPos + lineHeight * (vitalsLines.length + 1) > pageHeight - bottomMargin) {
          pdf.addPage();
          yPos = topMargin;
        }
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Vitals:', 20, yPos);
        yPos += lineHeight + 2;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        for (const line of vitalsLines) {
          if (yPos + lineHeight > pageHeight - bottomMargin) {
            pdf.addPage();
            yPos = topMargin;
          }
          pdf.text(line, 20, yPos);
          yPos += lineHeight;
        }
        yPos += lineHeight;
      }

      // Conclusion/Summary
      if (report.conclusion) {
        const writeTextLines = (lines: string[]) => {
          for (const line of lines) {
            if (yPos + lineHeight > pageHeight - bottomMargin) {
              pdf.addPage();
              yPos = topMargin;
            }
            pdf.text(String(line), 20, yPos);
            yPos += lineHeight;
          }
        };

        // Try to parse as JSON first
        const parsedSections = parseConclusion(report.conclusion);

        if (parsedSections && parsedSections.length > 0) {
          yPos = renderConclusionSections(
            pdf,
            parsedSections,
            yPos,
            pageWidth,
            pageHeight,
            topMargin,
            bottomMargin,
            lineHeight
          );
        } else {
          // Check if this is JSON with empty values (no consultation data)
          const isEmptyJsonSummary = (() => {
            try {
              const data = JSON.parse(report.conclusion);
              if (typeof data !== 'object' || data === null || Array.isArray(data)) return false;

              // Check if all value fields are empty
              const fieldsToCheck = [
                'patient_summary',
                'chief_complaint',
                'history_of_present_illness',
                'past_medical_surgical_history',
                'family_history',
                'physical_examination',
                'treatment_plan_management',
                'follow_up_plan',
                'assessment_and_diagnosis',
              ];

              for (const field of fieldsToCheck) {
                const value = data[field]?.value;
                if (value && typeof value === 'string' && value.trim() !== '') {
                  return false;
                }
              }

              // Check array fields
              const arrayFields = ['investigations_orders', 'medications'];
              for (const field of arrayFields) {
                const value = data[field]?.value;
                if (Array.isArray(value) && value.length > 0) {
                  return false;
                }
              }

              // If other fields (e.g. allergies/social_history) contain meaningful data, don't treat this as an empty summary.
              const ignoredKeys = new Set([
                ...fieldsToCheck,
                ...arrayFields,
                'savedAt',
                'autoSaved',
              ]);
              for (const [key, field] of Object.entries(data)) {
                if (ignoredKeys.has(key)) continue;
                const value = (field as { value?: unknown })?.value ?? field;
                if (
                  (typeof value === 'string' && value.trim() !== '') ||
                  (Array.isArray(value) && value.length > 0) ||
                  (typeof value === 'object' &&
                    value !== null &&
                    Object.keys(value as object).length > 0)
                ) {
                  return false;
                }
              }

              return true;
            } catch {
              return false;
            }
          })();

          if (isEmptyJsonSummary) {
            // Show friendly message for empty consultation summaries
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'italic');
            if (yPos + lineHeight > pageHeight - bottomMargin) {
              pdf.addPage();
              yPos = topMargin;
            }
            pdf.text('No clinical data available for this consultation.', 20, yPos);
            yPos += lineHeight;
          } else {
            // Fall back to plain text if not JSON or parsing failed
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Summary:', 20, yPos);
            yPos += lineHeight;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            const conclusionLines = pdf.splitTextToSize(
              stripHtml(report.conclusion),
              pageWidth - 40
            );
            writeTextLines(conclusionLines);
            yPos += lineHeight;
          }
        }
      }

      // Add separator line
      pdf.setLineWidth(0.5);
      pdf.line(20, yPos, 190, yPos);
      yPos += 15;
    });

    // Footer
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `Generated on ${new Date().toLocaleDateString()}`,
        20,
        pdf.internal.pageSize.height - 20
      );
      pdf.text(
        `Page ${i} of ${pageCount}`,
        pdf.internal.pageSize.width - 40,
        pdf.internal.pageSize.height - 20
      );
    }

    // Open PDF in a new tab
    const pdfBlob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    const newTab = window.open(blobUrl, '_blank', 'noopener,noreferrer');
    if (newTab) {
      newTab.opener = null;
      // Give the new tab plenty of time to load the blob URL before revoking it —
      // revoking too soon can leave the preview tab blank on slower browsers/devices.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      resolve();
    } else {
      // Popup was blocked — fall back to direct download, then revoke immediately
      // since pdf.save() doesn't depend on the blob URL.
      pdf.save(
        `${patientInfo?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Patient'}_Diagnostic_Reports_${new Date().toISOString().split('T')[0]}.pdf`
      );
      URL.revokeObjectURL(blobUrl);
      resolve();
    }
  });
};

/**
 * Generate a PDF from a case sheet's structured fields, using the same
 * patient-info/vitals/section-table rendering as generateDiagnosticReportsPDF
 * (medications and diagnosis codes render as tables via renderConclusionSections),
 * then open it in a new tab for preview — falling back to a direct download if
 * the popup is blocked. Mirrors the UX of the Patient Record per-visit download.
 */
export const generateCaseSheetPDF = (input: CaseSheetSummaryInput): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const {
        patient,
        consultationDate,
        fields,
        vitals,
        diet,
        lifestyle,
        viruddhaAhaara,
        isAyurveda,
        treatmentMedicinesTable,
        personalHistory,
      } = input;

      const pdf = new jsPDF();
      pdf.setFont('helvetica');

      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const leftMargin = 20;
      const topMargin = 30;
      const bottomMargin = 30;
      const lineHeight = 6;

      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Case Sheet Summary', leftMargin, topMargin);

      // Patient information
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      let yPos = topMargin + 16;
      const patientLineHeight = 7;
      pdf.text(`Patient: ${patient.name}`, leftMargin, yPos);
      yPos += patientLineHeight;
      pdf.text(`Patient ID: ${patient.identifier}`, leftMargin, yPos);
      yPos += patientLineHeight;
      if (patient.dateOfBirth) {
        pdf.text(`Date of Birth: ${patient.dateOfBirth}`, leftMargin, yPos);
        yPos += patientLineHeight;
      }
      if (patient.age >= 0 && patient.gender) {
        pdf.text(`Age/Gender: ${patient.age}/${patient.gender}`, leftMargin, yPos);
        yPos += patientLineHeight;
      }
      if (patient.phoneNumber) {
        pdf.text(`Phone: ${patient.phoneNumber}`, leftMargin, yPos);
        yPos += patientLineHeight;
      }
      const addressParts = [
        patient.address,
        patient.city,
        patient.state,
        patient.postalCode,
        patient.country,
      ].filter(Boolean);
      if (addressParts.length > 0) {
        pdf.text(`Address: ${addressParts.join(', ')}`, leftMargin, yPos);
        yPos += patientLineHeight;
      }
      pdf.text(`Consultation Date: ${consultationDate}`, leftMargin, yPos);
      yPos += 6;

      // Separator between patient details and clinical content
      pdf.setLineWidth(0.5);
      pdf.line(leftMargin, yPos, pageWidth - leftMargin, yPos);
      yPos += 12;

      // Vital Signs
      const vitalsLines = vitals ? formatVitals(vitals) : [];
      if (vitalsLines.length > 0) {
        if (yPos + lineHeight * (vitalsLines.length + 1) > pageHeight - bottomMargin) {
          pdf.addPage();
          yPos = topMargin;
        }
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Vitals:', leftMargin, yPos);
        yPos += lineHeight + 2;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        for (const line of vitalsLines) {
          if (yPos + lineHeight > pageHeight - bottomMargin) {
            pdf.addPage();
            yPos = topMargin;
          }
          pdf.text(line, leftMargin, yPos);
          yPos += lineHeight;
        }
        yPos += lineHeight;
      }

      // Clinical sections — same structured/table renderer used for diagnostic reports
      const sections = buildCaseSheetConclusionSections(
        fields,
        diet,
        lifestyle,
        viruddhaAhaara,
        isAyurveda,
        treatmentMedicinesTable,
        personalHistory
      );
      yPos = renderConclusionSections(
        pdf,
        sections,
        yPos,
        pageWidth,
        pageHeight,
        topMargin,
        bottomMargin,
        lineHeight
      );

      // Footer on every page
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(
          `Generated on ${new Date().toLocaleDateString()}`,
          leftMargin,
          pageHeight - bottomMargin + 10
        );
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 40, pageHeight - bottomMargin + 10);
      }

      const safeName = patient.name.replace(/[/\\:*?"<>|]/g, '_');
      const safeDate = consultationDate.replace(/[/\\:*?"<>|]/g, '_');

      // Open PDF in a new tab (same UX as generateDiagnosticReportsPDF)
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      const newTab = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (newTab) {
        newTab.opener = null;
        // Give the new tab plenty of time to load the blob URL before revoking it —
        // revoking too soon can leave the preview tab blank on slower browsers/devices.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        resolve();
      } else {
        // Popup was blocked — fall back to direct download, then revoke immediately
        // since pdf.save() doesn't depend on the blob URL.
        pdf.save(`CaseSheet_${safeName}_${safeDate}.pdf`);
        URL.revokeObjectURL(blobUrl);
        resolve();
      }
    } catch (err) {
      reject(err);
    }
  });
};
